import React from 'react';

import { cssMap } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import ErrorIcon from '@atlaskit/icon/core/status-error';
import { type Direction, FadeIn } from '@atlaskit/motion';
import { type Placement, Popper } from '@atlaskit/popper';
import Portal from '@atlaskit/portal';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { layers } from '@atlaskit/theme/constants';
import { token } from '@atlaskit/tokens';

const messages = defineMessages({
	errorLabel: {
		id: 'jira.jira.jira-platform-fields.error-label',
		defaultMessage: 'Error',
		description: 'Label shown for the validation message error icon.',
	},
});

const styles = cssMap({
	errorContainer: {
		display: 'flex',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		boxSizing: 'border-box',
		maxWidth: '368px',
		backgroundColor: token('elevation.surface.overlay'),
		boxShadow: token('elevation.shadow.overlay'),
		width: 'fit-content',
		borderRadius: token('radius.medium'),
	},
});

const ValidationContent = ({
	errorMessage,
	errorId,
}: {
	errorId?: string;
	errorMessage?: string;
}) => {
	const { formatMessage } = useIntl();
	return (
		<Box xcss={styles.errorContainer}>
			<Inline space="space.100" alignBlock="center">
				<ErrorIcon
					spacing="spacious"
					label={formatMessage(messages.errorLabel)}
					color={token('color.icon.danger')}
				/>
				{FeatureGates.getExperimentValue<boolean>('jira-aifc-a11y-fixes', 'isEnabled', false, {
					fireExperimentExposure: false,
				}) ? (
					<span id={errorId}>{errorMessage}</span>
				) : (
					errorMessage
				)}
			</Inline>
		</Box>
	);
};

/**
 * Converts a Popper placement to it's general direction.
 *
 * @param position - Popper Placement value, e.g. 'top-start'
 * @returns Popper Direction, e.g. 'top'
 */
const getDirectionFromPlacement = (placement: Placement): Direction =>
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	placement.split('-')[0] as Direction;

// Inverts motion direction
const invertedDirection = {
	top: 'bottom',
	bottom: 'top',
	left: 'right',
	right: 'left',
} as const;

export type ValidationTriggerProps = {
	errorId: string;
	ref: (node: HTMLElement | null) => void;
};

type ValidationPopupProps = {
	errorMessage?: string;
	placement?: Placement;
	showInvalidPopup: boolean;
	trigger: (props: ValidationTriggerProps) => React.ReactNode;
};

const POPPER_Z_INDEX = 9999;

export const ValidationPopup = ({
	showInvalidPopup,
	trigger,
	errorMessage,
	placement = 'right',
}: ValidationPopupProps): React.JSX.Element => {
	const [referenceElement, setReferenceElement] = React.useState<HTMLElement | null>(null);
	// One ID per instance of the popup
	const errorId = React.useId();

	return (
		<>
			{trigger({ ref: setReferenceElement, errorId })}

			{showInvalidPopup && referenceElement && (
				<Portal zIndex={POPPER_Z_INDEX}>
					<Popper referenceElement={referenceElement} placement={placement} strategy="fixed">
						{({ ref, style, placement: popperPlacement }) => (
							<FadeIn
								entranceDirection={invertedDirection[getDirectionFromPlacement(popperPlacement)]}
							>
								{({ className }) => (
									<div
										ref={ref}
										style={
											{
												// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
												...style,
												zIndex: layers.layer(),
											} as React.CSSProperties
										}
										data-placement={popperPlacement}
									>
										{/*  eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop  */}
										<div className={className} role="alert" aria-live="assertive">
											<ValidationContent errorMessage={errorMessage} errorId={errorId} />
										</div>
									</div>
								)}
							</FadeIn>
						)}
					</Popper>
				</Portal>
			)}
		</>
	);
};
